// Imports
// ********************************************
import {
  useSwrApi, useOnTabRefocus, ApiHeaderInfoOk,
} from '@hooks/useSwrApi';
import { useAppDispatch, useAppSelector } from 'src/app';
import { getUserHeaderInfo } from 'src/app/reducers/userSelector';
import { useState, useEffect } from 'react';
import { signOut } from 'next-auth/react';
import { useAuthClient } from '@hooks/index';


/**
 * @description Hook that fetches header info data and also saves it in Redux state for other uses,
 * even though a subscription to the same key (that means the fetch url) would be the same thing (you
 * access the swr cache). We directly check for the cookie here instead of relying on hooks in order
 * to avoid stale data on tab refocus checks.
 *
 * How it works:
 * 1. We check if the user is authenticated (has _rm/_rm_pbl cookies OR NextAuth session via API check)
 * 2. If yes, we subscribe (fetch) to the header info data. When not authenticated, the swr cache is
 *    emptied (mutated) and the data is cleared from Redux state.
 * 3. We also repeat the process on tab refocus (visibility change) and revalidate (re-fetch) the
 *    swr cache if the conditions inside the 'revalidateHeaderInfo' function are met. This guarantees that
 *    the data is always fresh, no matter if the user is authenticated or not and how many browser tabs
 *    are open.
 * 4. We can force check for the auth cookie. If the authentication cookie is valid, then it will re-fetch the
 *    swr data.
 *
 * Authentication detection:
 * - We check for _rm/_rm_pbl (Remember Me cookies) which are readable
 * - For NextAuth sessions (_ast), we use an internal API route since _ast is httpOnly
 * - If _rm cookies are not present, we check NextAuth session via /api/auth/check
 */
export const useHeaderInfoData = (locale = 'ro', fetchOnMount = true) => {
  // redux dispatch
  const appDispatch = useAppDispatch();
  const headerInfo = useAppSelector(getUserHeaderInfo);

  // Auth client helper functions
  const { checkAuthCookie, checkAuthStatus, getAuthCookieDate } = useAuthClient();

  // State for loading; checks for auth cookies (_rm/_rm_pbl) or NextAuth session via API
  // We directly check for the cookie here to avoid stale data.
  // Use existing headerInfo as a hint - if it exists, we're probably still authenticated
  // This prevents flickering when only _ast cookie exists (no _rm cookies)
  // ********************************************
  const [userAuthenticated, setUserAuthenticated] = useState(() => {
    if (!fetchOnMount) return false;
    // Fast check: if we have _rm cookies, we're authenticated
    const hasRmCookie = checkAuthCookie();
    if (hasRmCookie) return true;
    // Optimistic: if we have headerInfo in Redux, assume we're still authenticated
    // This prevents flickering - we'll verify via API check in the background
    // If headerInfo exists, start fetching immediately to avoid UI flicker
    return !!headerInfo;
  });
  const [isCheckingAuth, setIsCheckingAuth] = useState(() => {
    // Only need to check if we don't have _rm cookies and don't have headerInfo
    if (!fetchOnMount) return false;
    const hasRmCookie = checkAuthCookie();
    // If we have headerInfo, we're optimistically authenticated, but verify in background
    // If we don't have headerInfo and no _rm cookies, we need to check NextAuth
    return !hasRmCookie && !headerInfo;
  });

  // Check authentication on mount and when needed
  // ********************************************
  useEffect(() => {
    if (!isCheckingAuth) return;

    const checkAuth = async () => {
      // First check for _rm cookies (fast, client-side check)
      const hasRmCookie = checkAuthCookie();
      if (hasRmCookie) {
        setUserAuthenticated(true);
        setIsCheckingAuth(false);
        return;
      }

      // If no _rm cookies, check NextAuth session via API route
      // This handles the case where _rm cookies are removed but _ast still exists
      try {
        const isNextAuthAuthenticated = await checkAuthStatus();
        setUserAuthenticated(isNextAuthAuthenticated);
      } catch {
        // Only set to false if we don't have headerInfo (to avoid flickering)
        // If we have headerInfo, keep it true - the API will handle clearing it on 401
        if (!headerInfo) {
          setUserAuthenticated(false);
        }
      } finally {
        setIsCheckingAuth(false);
      }
    };

    void checkAuth();
  }, [isCheckingAuth, checkAuthCookie, checkAuthStatus, headerInfo]);

  // SWR: fetch data conditionally based on cookie presence
  // ********************************************
  const {
    error, isLoading, isValidating, mutate,
  } = useSwrApi<ApiHeaderInfoOk>({
    url: '/v1/header-info',
    shouldFetch: userAuthenticated,
    locale,
    apiOptions: {
      revalidateOnFocus: false,
      // On success, save the data in Redux state if it's valid
      onSuccess: (newData) => {
        // save header info in Redux state
        appDispatch({ type: 'user/saveHeaderInfo', payload: newData });
        // save auth date in Redux state => use it for comparison with current date from cookie
        appDispatch({ type: 'user/saveAuthDate', payload: getAuthCookieDate() });
      },
      // On error, clear the data from Redux state
      // 401 means user is not authenticated (no valid cookies)
      // Other errors might be temporary, but we still clear state to be safe
      onError: (errorData) => {
        appDispatch({ type: 'user/clearHeaderInfo' });

        if (!errorData || !errorData.errors?.length) {
          return;
        }

        const errorCode = errorData.errors[0].code;

        // For /v1/header-info a 401 error means user is not authenticated
        if (errorCode === 401) {
          // Sign out from NextAuth
          void signOut({ redirect: false }).then(() => {
            // Redirect to Symfony logout route to cleanup rm cookies
            window.location.href = '/logout';
          });
        }
      },
    },
  });

  // Run the callback on tab refocus. We directly check for the auth cookie to avoid stale data.
  // If there's no cookie, check NextAuth session via API route.
  // ********************************************
  useOnTabRefocus(() => {
    const hasRmCookie = checkAuthCookie();
    if (hasRmCookie) {
      setUserAuthenticated(true);
      return;
    }

    // Check NextAuth session via API (fire and forget)
    checkAuthStatus()
      .then((isNextAuthAuthenticated) => {
        setUserAuthenticated(isNextAuthAuthenticated);
      })
      .catch(() => {
        setUserAuthenticated(false);
      });
  });

  // Force check for the auth cookie and NextAuth session.
  // ********************************************
  const forceCheckAuthCookie = async () => {
    const hasRmCookie = checkAuthCookie();
    if (hasRmCookie) {
      setUserAuthenticated(true);
      return;
    }

    // Check NextAuth session via API
    try {
      const isNextAuthAuthenticated = await checkAuthStatus();
      setUserAuthenticated(isNextAuthAuthenticated);
    } catch {
      setUserAuthenticated(false);
    }
  };

  // Return data
  // ********************************************
  return {
    headerInfoData: headerInfo,
    headerInfoError: error,
    headerInfoLoading: isLoading,
    headerInfoValidating: isValidating,
    mutateHeaderInfo: mutate,
    forceCheckAuthCookie,
  };
};
